import assetUtils, { Asset } from 'app-lib/assetUtils'
import { memoize } from 'lodash'
import web3Utils from 'web3-utils'
import { chainConfigs, stakingConfig } from '../config/appConfig'
import { ChainId } from '../config/chains/chainCommon'
import { BZRXVestingTokenContract } from './typescript-wrappers/BZRXVestingToken'
import { BzxContract } from './typescript-wrappers/bzxContract'
import { erc20Contract } from './typescript-wrappers/erc20'
import { FixedSwapTokenConverterContract } from './typescript-wrappers/FixedSwapTokenConverter'
import { HelperContract } from './typescript-wrappers/helper'
import { iTokenContract } from './typescript-wrappers/iTokenContract'
import { MasterChefSushiContract } from './typescript-wrappers/MasterChefSushi'
import { MerkleDistributorContract } from './typescript-wrappers/MerkleDistributor'
import { oracleContract } from './typescript-wrappers/oracle'
import { StakingContract } from './typescript-wrappers/staking'
import { threePoolContract } from './typescript-wrappers/ThreePool'
import { VoteDelegatorContract } from './typescript-wrappers/voteDelegator'
import { WrappedNativeContract } from './typescript-wrappers/WrappedNative'

const threeCurveAddresses = new Map([[1, '0xbEbc44782C7dB0a1A60Cb6fe97d0b483032FF1C7']])

const vbzrxAddresses = new Map([[1, '0xB72B31907C1C95F3650b64b2469e08EdACeE5e8F']])

const wrappedNativeAddresses = new Map([
  [1, '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2'],
  [10, '0x4200000000000000000000000000000000000006'],
  [56, '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c'],
  [137, '0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270'],
  [42161, '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1'],
])

/**
 * Get address and throws error it is does not exist
 */
export function getAddress(chainId: ChainId, addresses: Map<number, string>): string {
  const address = addresses.get(chainId)
  if (!address) {
    throw new Error(`Contract address not found for chain ${chainId}`)
  }
  return address
}

export default class ContractsSource {
  private readonly provider: any
  public networkId: ChainId
  public canWrite: boolean

  public constructor(provider: any, networkId: ChainId, canWrite: boolean) {
    this.provider = provider
    this.networkId = networkId
    this.canWrite = canWrite
  }

  public getStakingContract = memoize(() => {
    return new StakingContract(stakingConfig.stakingContract, this.provider)
  })

  public getVoteDelegatorContract = memoize(() => {
    return new VoteDelegatorContract(stakingConfig.voteDelegatorContract, this.provider)
  })

  public getBzrxVestingContract = memoize(() => {
    const address = vbzrxAddresses.get(this.networkId) || ''
    return new BZRXVestingTokenContract(address.toLowerCase(), this.provider)
  })

  public getHelperContract = memoize(() => {
    const address = chainConfigs[this.networkId].contracts.helperProxy
    return new HelperContract(address.toLowerCase(), this.provider)
  })

  public getErc20Contract = memoize((addressOrAsset: string | Asset): erc20Contract => {
    const tokenAddress = web3Utils.isAddress(addressOrAsset)
      ? addressOrAsset
      : assetUtils.getAddress(this.networkId, addressOrAsset as Asset)
    return new erc20Contract(tokenAddress.toLowerCase(), this.provider)
  })

  public getITokenContract = memoize((underlyingAsset: Asset): iTokenContract => {
    const address = assetUtils.getITokenAddress(this.networkId, underlyingAsset)
    return new iTokenContract(address, this.provider)
  })

  public getITokenContractUsingIToken = memoize((iTokenAsset: Asset): iTokenContract => {
    const address = assetUtils.getAddress(this.networkId, iTokenAsset)
    return new iTokenContract(address, this.provider)
  })

  public getOracleContract = memoize((): oracleContract => {
    const address = chainConfigs[this.networkId].contracts.priceFeed
    return new oracleContract(address.toLowerCase(), this.provider)
  })

  public getiBZxContract = memoize((): BzxContract => {
    const address = chainConfigs[this.networkId].contracts.bzx
    return new BzxContract(address.toLowerCase(), this.provider)
  })

  public getThreePoolContract = memoize(() => {
    const address = getAddress(this.networkId, threeCurveAddresses)
    return new threePoolContract(address.toLowerCase(), this.provider)
  })

  public getMasterChefSushiContract = memoize(() => {
    return new MasterChefSushiContract(stakingConfig.masterChefSushiV2Contract, this.provider)
  })

  public getWrappedNativeContract = memoize((): WrappedNativeContract => {
    const address = getAddress(this.networkId, wrappedNativeAddresses)
    return new WrappedNativeContract(address, this.provider)
  })

  /**
   * For airdrops
   */
  public getMerkelDistributorContract = memoize((address: string) => {
    return new MerkleDistributorContract(address, this.provider)
  })

  public getFixedSwapConverter = memoize((address: string) => {
    return new FixedSwapTokenConverterContract(address, this.provider)
  })
}
